import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Hook for managing folder expansion state with auto-expand on hover
 *
 * Manages which folders are expanded in a tree view and provides auto-expand
 * functionality when hovering over a folder during drag operations (800ms delay).
 *
 * Features:
 * - Track expanded/collapsed state for folders
 * - Auto-expand folder after 800ms hover (useful for drag/drop)
 * - Proper cleanup of hover timers
 * - Toggle expansion state
 *
 * @param initialExpanded - Set of folder IDs that should start expanded
 * @returns Object with expanded state and control functions
 *
 * @example
 * ```tsx
 * const { isExpanded, toggleExpanded, startHoverExpand, cancelHoverExpand } = useFolderExpansion();
 *
 * return (
 *   <div
 *     onDragEnter={() => startHoverExpand(folderId)}
 *     onDragLeave={cancelHoverExpand}
 *   >
 *     {isExpanded(folderId) && <ChildFolders />}
 *   </div>
 * );
 * ```
 */
export function useFolderExpansion(initialExpanded: Set<string> = new Set()) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(initialExpanded);
  const hoverExpandTimer = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverExpandTimer.current) {
        clearTimeout(hoverExpandTimer.current);
      }
    };
  }, []);

  const isExpanded = useCallback(
    (folderId: string) => expandedFolders.has(folderId),
    [expandedFolders]
  );

  const toggleExpanded = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  const expandFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => new Set(prev).add(folderId));
  }, []);

  const collapseFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      newSet.delete(folderId);
      return newSet;
    });
  }, []);

  const startHoverExpand = useCallback((folderId: string, hasChildren: boolean) => {
    // Clear any existing timer
    if (hoverExpandTimer.current) {
      clearTimeout(hoverExpandTimer.current);
      hoverExpandTimer.current = null;
    }

    // Only set timer if folder has children
    if (hasChildren) {
      hoverExpandTimer.current = setTimeout(() => {
        setExpandedFolders((prev) => {
          // Check current state inside updater to avoid stale closure
          if (!prev.has(folderId)) {
            return new Set(prev).add(folderId);
          }
          return prev;
        });
        hoverExpandTimer.current = null;
      }, 800);
    }
  }, []);

  const cancelHoverExpand = useCallback(() => {
    if (hoverExpandTimer.current) {
      clearTimeout(hoverExpandTimer.current);
      hoverExpandTimer.current = null;
    }
  }, []);

  return {
    expandedFolders,
    isExpanded,
    toggleExpanded,
    expandFolder,
    collapseFolder,
    startHoverExpand,
    cancelHoverExpand,
  };
}
